import React, { useEffect, useRef, useMemo } from "react";

const sanitize = (value) => (typeof value === "string" ? value.trim() : "");

const sanitizeDescriptionHTML = (html) => {
    if (!html || typeof html !== "string") return "";

    // Extract content from nested sections before removing them
    // This preserves <p> tags and other content inside nested sections
    let cleaned = html;

    // Extract <p> tags from nested sections before removing section tags
    let extractedParagraphs = [];
    cleaned = cleaned.replace(/<section[^>]*>([\s\S]*?)<\/section>/gi, (match, content) => {
        // Extract all <p> tags from the nested section content
        const pTagMatches = content.match(/<p[^>]*>[\s\S]*?<\/p>/gi);
        if (pTagMatches && pTagMatches.length > 0) {
            extractedParagraphs.push(...pTagMatches);
        }
        // Remove the section
        return "";
    });

    // If we have extracted paragraphs and there's a <p id="cont3"> tag, insert them after it
    if (extractedParagraphs.length > 0 && cleaned.includes('id="cont3"')) {
        const extractedContent = extractedParagraphs.join('\n');
        cleaned = cleaned.replace(
            /(<p[^>]*id="cont3"[^>]*>)([\s\S]*?)(<\/p>)/gi,
            (match, openTag, innerContent, closeTag) => {
                // Insert the extracted paragraphs as siblings after the cont3 paragraph
                return openTag + innerContent + closeTag + '\n' + extractedContent;
            }
        );
    } else if (extractedParagraphs.length > 0) {
        // If no cont3 found, just append the extracted content
        cleaned = cleaned + '\n' + extractedParagraphs.join('\n');
    }

    // Remove any remaining empty section tags
    cleaned = cleaned.replace(/<section[^>]*\/?>/gi, "");

    // Clean up multiple consecutive whitespace/newlines
    cleaned = cleaned.replace(/\n\s*\n\s*\n/g, "\n").trim();

    return cleaned;
};

const TimelineSection = ({ fields, sectionId }) => {
    const rootRef = useRef(null);

    const sanitizedData = useMemo(() => {
        if (!fields) return null;

        const eyebrow = sanitize(fields.eyebrow || "");
        const title = sanitize(fields.title || "");
        const items = Array.isArray(fields.items)
            ? fields.items
                .map((item) => {
                    const year = sanitize(item?.year || "");
                    const heading = sanitize(item?.heading || "");
                    const rawDescription = item?.description || "";
                    const description = sanitizeDescriptionHTML(rawDescription);

                    // Explicitly handle false case - no image should be shown
                    let imageUrl = null;
                    let imageAlt = "";
                    if (item?.image === false) {
                        imageUrl = null;
                    } else if (item?.image?.url) {
                        const url = sanitize(item.image.url);
                        imageUrl = url || null;
                        imageAlt = sanitize(item.image.alt || "");
                    }

                    if (!year && !heading && !description) return null;

                    return { year, heading, description, imageUrl, imageAlt };
                })
                .filter(Boolean)
            : [];

        const hasContent = eyebrow || title || items.length > 0;
        if (!hasContent) return null;

        return { eyebrow, title, items };
    }, [fields]);

    useEffect(() => {
        if (!sanitizedData || !rootRef.current) return undefined;

        const root = rootRef.current;
        const timeline = root.querySelector(".timeline");
        const progressFill = root.querySelector(".progress-fill");
        const progressDot = root.querySelector(".progress-dot");
        const items = Array.from(root.querySelectorAll(".timeline-item"));
        const dots = Array.from(root.querySelectorAll(".timeline-dot"));
        const line = root.querySelector(".timeline-progress");

        if (!timeline || !progressFill || !progressDot || !items.length || !dots.length || !line) return undefined;

        let rafId = null;
        let isRunning = true;

        // Smooth interpolation values
        let currentDotY = 0;
        let targetDotY = 0;

        // Scroll tracking variables
        let sectionTop = 0;
        let sectionBottom = 0;
        let scrollStartPoint = 0; // When section is 200px from top
        let scrollEndPoint = 0;
        let timelineLineHeight = 0;

        // Dot bounds - positions of first and last static timeline dots
        let firstDotY = 0; // Y position of first dot relative to timeline line
        let lastDotY = 0;  // Y position of last dot relative to timeline line

        // Store all dot positions for slowdown effect
        let dotPositions = []; // Array of Y positions of all dots relative to timeline line

        const calculateScrollBounds = () => {
            const rect = root.getBoundingClientRect();
            const scrollY = window.scrollY || window.pageYOffset;

            // Get absolute positions
            sectionTop = scrollY + rect.top;
            sectionBottom = scrollY + rect.bottom;

            // Start tracking when section top reaches the middle of viewport (50% from top)
            scrollStartPoint = sectionTop - (window.innerHeight / 2);

            // End tracking when section bottom reaches viewport bottom
            // This ensures the dot completes when the timeline section is fully scrolled
            scrollEndPoint = sectionBottom - window.innerHeight;

            // If scrollEndPoint is less than scrollStartPoint, adjust it
            if (scrollEndPoint <= scrollStartPoint) {
                scrollEndPoint = sectionBottom; // Fallback to section bottom
            }

            // Get timeline line height
            if (line) {
                timelineLineHeight = line.offsetHeight;
            }

            // Calculate first and last dot positions relative to timeline line
            // Also store all dot positions for slowdown effect
            if (line && dots.length > 0) {
                const lineRect = line.getBoundingClientRect();
                dotPositions = []; // Reset array

                // Calculate all dot positions
                dots.forEach((dot) => {
                    const dotRect = dot.getBoundingClientRect();
                    const dotCenterY = dotRect.top + dotRect.height / 2;
                    const relativeY = dotCenterY - lineRect.top;
                    dotPositions.push(relativeY);
                });

                // First dot position
                firstDotY = dotPositions[0] || 0;

                // Last dot position
                lastDotY = dotPositions[dotPositions.length - 1] || 0;
            }

        };

        const updateTimelineFromScroll = () => {
            // This function is called from animate() which already runs in RAF loop
            // No need to wrap in RAF again - just batch layout reads
            const scrollY = window.scrollY || window.pageYOffset;

            // Calculate the range between first and last dot
            const dotRange = lastDotY - firstDotY;

            // Cache offsetHeight to avoid multiple reads
            const progressDotHalfHeight = progressDot ? progressDot.offsetHeight / 2 : 0;

            // Calculate scroll progress
            if (scrollY < scrollStartPoint) {
                // Before tracking starts - position at first dot
                targetDotY = firstDotY - progressDotHalfHeight;
            } else if (scrollY > scrollEndPoint) {
                // After tracking ends - position at last dot
                targetDotY = lastDotY - progressDotHalfHeight;
            } else {
                // During tracking - keep dot at center of viewport
                // Calculate where the center of viewport intersects with the timeline line
                if (line) {
                    // Batch read: get layout property once
                    const lineRect = line.getBoundingClientRect();
                    const viewportCenterY = window.innerHeight / 2;

                    // Calculate the Y position relative to the timeline line where viewport center is
                    const centerYRelativeToLine = viewportCenterY - lineRect.top;

                    // Map scroll progress to determine fill height
                    const scrollRange = scrollEndPoint - scrollStartPoint;
                    const rawScrollProgress = (scrollY - scrollStartPoint) / scrollRange;
                    const clampedRawProgress = Math.max(0, Math.min(1, rawScrollProgress));

                    // Apply slowdown effect near timeline dots
                    // This creates a pause/slowdown when approaching each milestone
                    const slowdownDistance = 100; // Pixels around each dot to slow down (increased for better effect)
                    const slowdownFactor = 0.4; // How much to slow down (0.4 = 60% slower, creates noticeable pause)

                    let adjustedProgress = clampedRawProgress;

                    // Check proximity to each dot based on current fill position (not dot position)
                    // This ensures the slowdown happens as we approach each milestone
                    // EXCLUDE first and last dots - only pause at middle milestones
                    if (dotPositions.length > 2) { // Need at least 3 dots to have middle ones
                        // Use the raw progress to estimate where we are on the timeline
                        const estimatedFillY = firstDotY + (clampedRawProgress * dotRange);

                        let closestDotDist = Infinity;
                        let isNearDot = false;

                        // Only check middle dots (exclude first index 0 and last index)
                        for (let i = 1; i < dotPositions.length - 1; i++) {
                            const dotY = dotPositions[i];
                            const dist = Math.abs(estimatedFillY - dotY);
                            if (dist < slowdownDistance) {
                                isNearDot = true;
                                closestDotDist = Math.min(closestDotDist, dist);
                            }
                        }

                        // Apply slowdown when near a dot
                        if (isNearDot) {
                            // Calculate slowdown intensity (stronger when closer, max at dot center)
                            const slowdownIntensity = 1 - (closestDotDist / slowdownDistance);
                            const effectiveSlowdown = slowdownFactor * slowdownIntensity;

                            // Create a smooth slowdown curve - stronger pause when very close
                            const pauseStrength = Math.pow(slowdownIntensity, 1.5); // Exponential curve for smoother effect

                            // Reduce progress change when near dot (creates pause effect)
                            // This makes the visual progression slower through the milestone area
                            adjustedProgress = clampedRawProgress * (1 - effectiveSlowdown * pauseStrength);
                        }
                    }

                    // Ensure adjusted progress stays within bounds
                    adjustedProgress = Math.max(0, Math.min(1, adjustedProgress));

                    // Keep dot at viewport center, but clamp to first/last dot bounds
                    const minCenterY = firstDotY;
                    const maxCenterY = lastDotY;
                    const clampedCenterY = Math.max(minCenterY, Math.min(centerYRelativeToLine, maxCenterY));

                    // If viewport center is within timeline bounds, use it; otherwise use progress-based position
                    let targetY;
                    if (centerYRelativeToLine >= firstDotY && centerYRelativeToLine <= lastDotY) {
                        // Viewport center is within bounds - keep dot at center
                        targetY = centerYRelativeToLine;
                    } else if (centerYRelativeToLine < firstDotY) {
                        // Viewport center is above first dot - use first dot
                        targetY = firstDotY;
                    } else {
                        // Viewport center is below last dot - use last dot
                        targetY = lastDotY;
                    }

                    // Set target position (center the dot on the target Y)
                    targetDotY = targetY - progressDotHalfHeight;

                    // Final clamp to ensure dot stays within first and last dot bounds
                    const minDotY = firstDotY - progressDotHalfHeight;
                    const maxDotY = lastDotY - progressDotHalfHeight;

                    targetDotY = Math.max(minDotY, Math.min(targetDotY, maxDotY));
                }
            }
        };

        const updateActiveStates = () => {
            if (!line || !progressDot) return;

            // Cache offsetHeight to avoid multiple reads
            const progressDotHalfHeight = progressDot.offsetHeight / 2;
            // Get current dot center position
            const currentDotCenterY = currentDotY + progressDotHalfHeight;

            // Batch read: get all layout properties once before loop
            const lineRect = line.getBoundingClientRect();
            const dotRects = Array.from(dots).map(dot => dot.getBoundingClientRect());

            // Find which static dot is closest to current progress dot position
            let closestIndex = 0;
            let minDist = Infinity;

            dots.forEach((dot, i) => {
                // Use cached rect instead of reading again
                const dotRect = dotRects[i];
                const dotCenterY = dotRect.top + dotRect.height / 2;
                const relativeY = dotCenterY - lineRect.top;
                const dist = Math.abs(relativeY - currentDotCenterY);

                if (dist < minDist) {
                    minDist = dist;
                    closestIndex = i;
                }
            });

            // Update active states
            dots.forEach((dot, i) => {
                if (dot) {
                    dot.classList.toggle("active", i === closestIndex);
                }
            });

            // Update active year
            items.forEach((item) => item.classList.remove("active-year"));
            if (items[closestIndex]) {
                items[closestIndex].classList.add("active-year");
            }
        };

        // Smooth animation loop with interpolation
        const animate = () => {
            if (!isRunning) return;

            // Recalculate bounds (handles dynamic content and resize)
            calculateScrollBounds();

            // Update target positions based on scroll
            updateTimelineFromScroll();

            // Smooth interpolation using easing - make it more responsive during scroll
            const diffDot = targetDotY - currentDotY;

            // Use higher easing for faster response, but still smooth
            // If difference is large, use higher easing to catch up faster
            const absDiff = Math.abs(diffDot);
            let easing = 0.25; // Default easing

            if (absDiff > 50) {
                easing = 0.4; // Faster when far behind
            } else if (absDiff > 20) {
                easing = 0.3; // Medium speed
            }

            // Only update if difference is significant (prevents jitter)
            if (absDiff > 0.1) {
                currentDotY += diffDot * easing;
            } else {
                currentDotY = targetDotY;
            }

            // Apply smooth positions - update dot and fill together using exact same position
            if (progressDot && progressFill && line) {
                // Cache layout properties once to avoid forced reflow
                const progressDotHalfHeight = progressDot.offsetHeight / 2;
                const lineHeight = line.offsetHeight;

                // Calculate dot center position (use exact same calculation)
                const dotCenterY = currentDotY + progressDotHalfHeight;

                // Batch write: update all styles together
                progressDot.style.top = currentDotY + "px";
                progressFill.style.bottom = (lineHeight - dotCenterY) + "px";
                progressFill.style.height = dotCenterY + "px";
            } else if (progressDot) {
                // Fallback if fill or line not available
                progressDot.style.top = currentDotY + "px";
            }

            // Update active states based on current position
            updateActiveStates();

            rafId = requestAnimationFrame(animate);
        };

        // Handle resize
        const handleResize = () => {
            // Recalculate bounds on resize
            calculateScrollBounds();
        };

        // Initialize bounds and positions
        calculateScrollBounds();
        updateTimelineFromScroll();

        // Initialize current positions to target positions to avoid jump
        if (progressDot && progressFill && line) {
            currentDotY = targetDotY;

            // Set initial positions
            progressDot.style.top = currentDotY + "px";

            // Set fill to extend upward from dot (fill always follows dot position)
            const dotCenterY = currentDotY + (progressDot.offsetHeight / 2);
            progressFill.style.bottom = (line.offsetHeight - dotCenterY) + "px";
            progressFill.style.height = dotCenterY + "px";
        }

        // Start animation loop (handles scroll updates smoothly)
        animate();

        // Add event listeners
        window.addEventListener("resize", handleResize, { passive: true });

        return () => {
            isRunning = false;
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            window.removeEventListener("resize", handleResize);
        };
    }, [sanitizedData]);

    if (!sanitizedData) return null;

    const { eyebrow, title, items } = sanitizedData;

    return (
        <section className="timeline-section" ref={rootRef} data-aos="false">
            <div className="canvas">
                <div className="timeline-inner">
                    {(eyebrow || title) && (
                        <div className="timeline-header" data-stagger-index="0">
                            {eyebrow && <p className="small-title">{eyebrow}</p>}
                            {title && <h2>{title}</h2>}
                        </div>
                    )}

                    {items.length > 0 && (
                        <div className="timeline">
                            <div className="timeline-progress">
                                <div className="progress-fill"></div>
                                <div className="progress-dot"></div>
                            </div>

                            {items.map((item, index) => (
                                <div className="timeline-item" id={`timeline-${index + 1}`} key={`timeline-${index}`} data-stagger-index={index + 1}>
                                    {item.year && <div className="timeline-year">{item.year}</div>}
                                    <div className="timeline-dot"></div>

                                    <div className="timeline-content">
                                        {item.heading && <h3>{item.heading}</h3>}
                                        {item.description && typeof item.description === "string" && item.description.length > 0 && (
                                            <div
                                                dangerouslySetInnerHTML={{ __html: item.description }}
                                                id={index === 2 ? "cont3" : index === 1 ? "cont2" : undefined}
                                            />
                                        )}

                                        {item.imageUrl && typeof item.imageUrl === "string" && item.imageUrl.length > 0 && (
                                            <div className="imgs">
                                                <img src={item.imageUrl} alt={item.imageAlt || item.heading || `Timeline ${index + 1}`} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default TimelineSection;

